/**
 * ChecklistHistoryTab.js
 *
 * FT-310 (2026-07-08) — Browsable historical checklists view.
 *
 * Purpose
 * -------
 * The ChecklistsTab already surfaces a "Recent Completions" strip (last 3-5
 * items) for at-a-glance quick access. That's deliberately shallow — it's
 * not a browser, it's a nudge. When Ryan or Steven need to answer "what
 * checks did we run in the last 30 days?" or "who signed off the last
 * Watch Handover before the incident?", they need a real historical view.
 *
 * This tab renders every completion record for the current vessel with
 * three client-side filters (date range preset, checklist type, name
 * substring), sorted newest-first, paginated in batches of 20. Clicking
 * a row opens the existing `CompletedChecklistView` modal in read-only
 * mode — no new detail-view surface to maintain.
 *
 * Data source
 * -----------
 * Reads from the eagerly-mounted `useChecklistRecordsContext` (FT-324
 * cycle 3) — a single hook instance lives at OperationsModule level. No
 * fresh mount here, no race conditions, no local storage reads. All
 * filtering is in-memory on the already-hydrated array.
 *
 * Filter persistence
 * ------------------
 * Filter selections persist per-vessel via `useStorageState` so they
 * survive tab switches, browser reloads and vessel switches. Key is
 * scoped to the vessel automatically by useStorageState. Jonathan
 * confirmed this UX call during FT-310 scoping.
 *
 * Deliberately deferred (out of MVP scope, confirmed with Jonathan)
 * -----------------------------------------------------------------
 * - Print/PDF export from detail view — future ticket.
 * - Column-header click sort — default DESC by completedAt is enough
 *   for MVP; sort UI is easy to add later without schema change.
 */

import React, { useMemo, useState, useCallback } from 'react';
import {
  CheckCircle,
  AlertTriangle,
  ChevronRight,
  Search,
  Filter,
  X
} from 'lucide-react';

import { useChecklistRecordsContext } from '../../../../hooks/domain/operations/ChecklistRecordsContext';
import { useStorageState } from '../../../../hooks/useStorageState';
import { CHECKLIST_TYPES } from '../../../../schemas/checklistCompletionSchema';
import Dropdown from '../../../common/Dropdown';

import CompletedChecklistView from './CompletedChecklistView';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PAGE_SIZE = 20;

/** Date-range preset options — keyed for storage stability. */
const DATE_PRESETS = [
  { key: '7d', label: 'Last 7 days', days: 7 },
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', days: 90 },
  { key: 'all', label: 'All time', days: null },
  { key: 'custom', label: 'Custom', days: null }
];

/** Human-readable labels for each checklist type. */
const TYPE_LABELS = {
  [CHECKLIST_TYPES.DAILY_CHECK]: 'Daily Check',
  [CHECKLIST_TYPES.OPERATIONAL]: 'Operational',
  [CHECKLIST_TYPES.PERMIT_TO_WORK]: 'Permit to Work',
  [CHECKLIST_TYPES.PERIODIC_INSPECTION]: 'Periodic Inspection',
  [CHECKLIST_TYPES.COMPANY]: 'Company'
};

/** Tailwind badge palette per type — kept local so no app-wide theme edits. */
const TYPE_BADGE_CLASSES = {
  [CHECKLIST_TYPES.DAILY_CHECK]: 'bg-blue-100 text-blue-700',
  [CHECKLIST_TYPES.OPERATIONAL]: 'bg-amber-100 text-amber-700',
  [CHECKLIST_TYPES.PERMIT_TO_WORK]: 'bg-red-100 text-red-700',
  [CHECKLIST_TYPES.PERIODIC_INSPECTION]: 'bg-purple-100 text-purple-700',
  [CHECKLIST_TYPES.COMPANY]: 'bg-slate-100 text-slate-700'
};

const FALLBACK_BADGE = 'bg-gray-100 text-gray-700';

export const DEFAULT_FILTERS = {
  datePreset: 'all',
  customFrom: '',   // YYYY-MM-DD
  customTo: '',     // YYYY-MM-DD
  type: 'all',      // 'all' or a CHECKLIST_TYPES value
  nameQuery: ''
};

// ---------------------------------------------------------------------------
// Pure filter helper (exported for unit tests)
// ---------------------------------------------------------------------------

/**
 * Filter + sort a records array based on the current filter state.
 * Pure — no React, no storage, no clock reads beyond `now`.
 *
 * @param {Array} records
 * @param {Object} filters       - shape of DEFAULT_FILTERS
 * @param {Date}   now           - injectable "now" for testability
 * @returns {Array} filtered, sorted newest-first
 */
export const applyChecklistHistoryFilters = (records, filters, now = new Date()) => {
  if (!Array.isArray(records)) return [];

  const {
    datePreset = 'all',
    customFrom = '',
    customTo = '',
    type = 'all',
    nameQuery = ''
  } = filters || {};

  const preset = DATE_PRESETS.find(p => p.key === datePreset) || DATE_PRESETS[3];

  let fromMs = null;
  let toMs = null;
  if (preset.key === 'custom') {
    if (customFrom) {
      const d = new Date(`${customFrom}T00:00:00`);
      if (!Number.isNaN(d.getTime())) fromMs = d.getTime();
    }
    if (customTo) {
      // Inclusive end-of-day
      const d = new Date(`${customTo}T23:59:59.999`);
      if (!Number.isNaN(d.getTime())) toMs = d.getTime();
    }
  } else if (preset.days != null) {
    fromMs = now.getTime() - preset.days * 24 * 60 * 60 * 1000;
  }

  const nameNeedle = (nameQuery || '').trim().toLowerCase();

  const filtered = records.filter(r => {
    // Exclude closure records (r.type is set for permit-closure entries; those
    // have no checklistType and would render as a broken row).
    if (!r || !r.checklistType || r.type) return false;

    // Type filter
    if (type !== 'all' && r.checklistType !== type) return false;

    // Date range
    if (fromMs != null || toMs != null) {
      const ts = r.completedAt ? new Date(r.completedAt).getTime() : NaN;
      if (Number.isNaN(ts)) return false;
      if (fromMs != null && ts < fromMs) return false;
      if (toMs != null && ts > toMs) return false;
    }

    // Name substring (case-insensitive)
    if (nameNeedle) {
      const haystack = (r.checklistName || '').toLowerCase();
      if (!haystack.includes(nameNeedle)) return false;
    }

    return true;
  });

  filtered.sort((a, b) => {
    const at = a.completedAt ? new Date(a.completedAt).getTime() : 0;
    const bt = b.completedAt ? new Date(b.completedAt).getTime() : 0;
    return bt - at;
  });

  return filtered;
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const TypeBadge = ({ type }) => {
  const label = TYPE_LABELS[type] || type || 'Unknown';
  const cls = TYPE_BADGE_CLASSES[type] || FALLBACK_BADGE;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
};

const StatusPill = ({ record }) => {
  const checks = Array.isArray(record.checks) ? record.checks : [];
  const total = checks.length;
  if (total === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-slate-500">
        <CheckCircle className="h-3.5 w-3.5" />
        Recorded
      </span>
    );
  }
  const allPassed = checks.every(c => c && c.passed);
  if (allPassed) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-green-700">
        <CheckCircle className="h-3.5 w-3.5" />
        All passed
      </span>
    );
  }
  const failed = checks.filter(c => !c || !c.passed).length;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-amber-700">
      <AlertTriangle className="h-3.5 w-3.5" />
      {failed} failed
    </span>
  );
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

/**
 * Row for medium/large screens (table body).
 * Data cells only — the surrounding <table> is defined by the parent so we can
 * share styling with the header row.
 */
const HistoryRow = ({ record, onOpen }) => (
  <tr
    onClick={() => onOpen(record)}
    className="cursor-pointer hover:bg-slate-50 transition-colors"
    data-testid="checklist-history-row"
  >
    <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">{formatDate(record.completedAt)}</td>
    <td className="px-4 py-3 text-sm text-slate-800 font-medium">{record.checklistName || 'Untitled'}</td>
    <td className="px-4 py-3"><TypeBadge type={record.checklistType} /></td>
    <td className="px-4 py-3 text-sm text-slate-600">{record.completedBy?.name || '—'}</td>
    <td className="px-4 py-3"><StatusPill record={record} /></td>
    <td className="px-4 py-3 text-right">
      <ChevronRight className="h-4 w-4 text-slate-400 inline-block" />
    </td>
  </tr>
);

/**
 * Card view for narrow screens (Ryan trials on a Moto G06 —
 * table columns get cramped below ~640px).
 */
const HistoryCard = ({ record, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(record)}
    className="w-full text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-cyan-400 hover:shadow-md transition-all"
    data-testid="checklist-history-card"
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <p className="font-medium text-slate-800 truncate">{record.checklistName || 'Untitled'}</p>
        <p className="text-xs text-slate-500 mt-1">{formatDate(record.completedAt)}</p>
        <p className="text-xs text-slate-500 mt-0.5">{record.completedBy?.name || '—'}</p>
      </div>
      <ChevronRight className="h-4 w-4 text-slate-400 mt-1 flex-shrink-0" />
    </div>
    <div className="mt-3 flex items-center justify-between">
      <TypeBadge type={record.checklistType} />
      <StatusPill record={record} />
    </div>
  </button>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const ChecklistHistoryTab = () => {
  const { records } = useChecklistRecordsContext();

  // Filters — persisted per-vessel via useStorageState (auto-scopes to vesselId).
  // Storage key deliberately fixed; useStorageState appends the vessel scope.
  const { value: filters, setValue: setFilters } = useStorageState(
    'checklist_history_filters',
    DEFAULT_FILTERS
  );

  // Defensive: fall back to defaults if storage returns a non-object
  // (paranoia against rogue hydrate events — same pattern used inside
  // useChecklistRecords for the raw records array).
  const activeFilters = useMemo(() => ({
    ...DEFAULT_FILTERS,
    ...(filters && typeof filters === 'object' ? filters : {})
  }), [filters]);

  const [viewingRecord, setViewingRecord] = useState(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Reset pagination whenever filters change — otherwise a filter that shrinks
  // the list to 2 items would still hide the "Load More" for the next filter
  // change that expands it back.
  const filterSignature = JSON.stringify(activeFilters);
  React.useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filterSignature]);

  const filtered = useMemo(
    () => applyChecklistHistoryFilters(records, activeFilters),
    [records, activeFilters]
  );

  const visible = useMemo(
    () => filtered.slice(0, visibleCount),
    [filtered, visibleCount]
  );

  const hasMore = visible.length < filtered.length;

  const updateFilter = useCallback((patch) => {
    setFilters(prev => ({
      ...DEFAULT_FILTERS,
      ...(prev && typeof prev === 'object' ? prev : {}),
      ...patch
    }));
  }, [setFilters]);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, [setFilters]);

  const filtersAreActive =
    activeFilters.datePreset !== 'all' ||
    activeFilters.type !== 'all' ||
    activeFilters.nameQuery.trim() !== '' ||
    activeFilters.customFrom !== '' ||
    activeFilters.customTo !== '';

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-600">
            <Filter className="h-4 w-4" />
            Filters
          </div>
          {filtersAreActive && (
            <button
              type="button"
              onClick={resetFilters}
              className="text-xs text-cyan-600 hover:text-cyan-700 font-medium flex items-center gap-1"
              data-testid="checklist-history-reset"
            >
              <X className="h-3.5 w-3.5" />
              Reset
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Date preset */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Date range</label>
            <div data-testid="checklist-history-date-preset">
              <Dropdown
                value={activeFilters.datePreset}
                onChange={(val) => updateFilter({ datePreset: val })}
                width="w-full"
                options={DATE_PRESETS.map(p => ({ value: p.key, label: p.label }))}
              />
            </div>
          </div>

          {/* Type */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Type</label>
            <div data-testid="checklist-history-type">
              <Dropdown
                value={activeFilters.type}
                onChange={(val) => updateFilter({ type: val })}
                width="w-full"
                options={[
                  { value: 'all', label: 'All types' },
                  ...Object.values(CHECKLIST_TYPES).map(t => ({
                    value: t,
                    label: TYPE_LABELS[t] || t
                  }))
                ]}
              />
            </div>
          </div>

          {/* Name search */}
          <div className="sm:col-span-2 lg:col-span-2">
            <label className="block text-xs text-slate-500 mb-1">Checklist name</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <input
                type="text"
                aria-label="Filter by checklist name"
                value={activeFilters.nameQuery}
                onChange={(e) => updateFilter({ nameQuery: e.target.value })}
                placeholder="Search by name…"
                className="w-full pl-8 pr-3 py-2 text-sm border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                data-testid="checklist-history-name"
              />
            </div>
          </div>
        </div>

        {/* Custom date range inputs — only when preset === 'custom' */}
        {activeFilters.datePreset === 'custom' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            <div>
              <label className="block text-xs text-slate-500 mb-1">From</label>
              <input
                type="date"
                aria-label="Custom range from"
                value={activeFilters.customFrom}
                onChange={(e) => updateFilter({ customFrom: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                data-testid="checklist-history-from"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">To</label>
              <input
                type="date"
                aria-label="Custom range to"
                value={activeFilters.customTo}
                onChange={(e) => updateFilter({ customTo: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                data-testid="checklist-history-to"
              />
            </div>
          </div>
        )}
      </div>

      {/* Result count */}
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span data-testid="checklist-history-count">
          {filtered.length} {filtered.length === 1 ? 'record' : 'records'}
          {filtered.length > 0 && ` (showing ${visible.length})`}
        </span>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div
          className="text-center py-12 bg-white border border-dashed border-slate-300 rounded-xl"
          data-testid="checklist-history-empty"
        >
          <CheckCircle className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-600 font-medium">
            No completions match the filters
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {filtersAreActive
              ? 'Try widening the date range or clearing filters.'
              : 'Complete a checklist and it will appear here.'}
          </p>
          {filtersAreActive && (
            <button
              type="button"
              onClick={resetFilters}
              className="mt-3 text-sm text-cyan-600 hover:text-cyan-700 font-medium"
            >
              Reset filters
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Table view — sm and up */}
          <div className="hidden sm:block overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  {['Date', 'Checklist', 'Type', 'Completed by', 'Status'].map(h => (
                    <th key={h} scope="col" className="px-4 py-2.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">{h}</th>
                  ))}
                  <th scope="col" className="px-4 py-2.5" aria-label="Open" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {visible.map(r => (
                  <HistoryRow key={r.id} record={r} onOpen={setViewingRecord} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Card view — below sm */}
          <div className="sm:hidden space-y-3">
            {visible.map(r => (
              <HistoryCard key={r.id} record={r} onOpen={setViewingRecord} />
            ))}
          </div>

          {/* Load More */}
          {hasMore && (
            <div className="flex justify-center pt-2">
              <button
                type="button"
                onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
                className="px-4 py-2 text-sm font-medium text-cyan-700 bg-cyan-50 hover:bg-cyan-100 rounded-lg transition-colors"
                data-testid="checklist-history-load-more"
              >
                Load more ({filtered.length - visible.length} remaining)
              </button>
            </div>
          )}
        </>
      )}

      {/* Detail modal — reuses the existing full-screen CompletedChecklistView */}
      {viewingRecord && (
        <CompletedChecklistView
          record={viewingRecord}
          onClose={() => setViewingRecord(null)}
        />
      )}
    </div>
  );
};

export default ChecklistHistoryTab;
